/*
 * QUARANTINE COLOUR (2026-07-26, value updated 2026-07-28). Quarantined renders
 * GovernanceColors.quarantined (now #A78BFA, see ui/config/governanceColors.ts header note)
 * and the --status-quarantine token (ui/globals.css:22) -- NOT the #FBBF24 warning colour it
 * used to share with Monitored/flagged. A quarantined agent is CONTAINED; a flagged one is
 * merely watched. Painting both amber made two different governance states visually
 * indistinguishable in the one view whose job is to tell them apart.
 *
 * The token was declared and pinned by ui/test/command-center.test.tsx (quarantined !==
 * critical) but consumed by NOTHING -- these three call sites are the first real consumers.
 * The brand handoff specifies quarantine twice and contradicts itself (status table: red with
 * critical; Agent-Registry mock: amber with flagged), which is open with Trey; the product's
 * semantic token is treated as authority so the marketing mock and the shipped UI agree.
 */
import { GovernanceColors } from '@/config/governanceColors';
import { useFleetData } from '@/hooks/useCommandCenter';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Users, Zap, CheckCircle, XCircle, Layers } from 'lucide-react';
import { TierFeatureGrid } from './TierFeatureGrid';

interface FleetViewProps {
  demoActive?: boolean;
  demoStep?: number;
  demoStepKind?: string | undefined;
}

// Map a demo step kind to the set of pipeline stages that should glow during that step.
// Stages are 1-indexed to match the visualization labels.
// authorize-denied: stages 1,2,3 fire then stage 3 DENIES the request (red glow)
// audit-recorded: stage 8 (Evidence Recording) writes to chain (green glow)
// halt-fleet: stage 10 (Halt Evaluation) executes the halt (amber glow)
// resume-fleet: stage 10 again with cyan
// demo-complete: all stages pulse green
function activeStagesForKind(kind?: string): { active: Set<number>; deny: number | null; color: string } {
  switch (kind) {
    case 'authorize-denied':
      return { active: new Set([1, 2, 3]), deny: 3, color: GovernanceColors.critical };
    case 'audit-recorded':
      return { active: new Set([8]), deny: null, color: GovernanceColors.healthy };
    case 'halt-fleet':
      // PIPELINE-STAGE colour, not agent status -- intentionally left as raw hex, see report.
      return { active: new Set([10]), deny: null, color: '#FBBF24' };
    case 'resume-fleet':
      return { active: new Set([10]), deny: null, color: GovernanceColors.streaming };
    case 'demo-complete':
      return { active: new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]), deny: null, color: GovernanceColors.healthy };
    default:
      return { active: new Set(), deny: null, color: GovernanceColors.primary };
  }
}

export function FleetView({ demoActive, demoStepKind }: FleetViewProps = {}) {
  const { data, loading } = useFleetData();
  const stageHighlight = activeStagesForKind(demoActive ? demoStepKind : undefined);
  const claw = data?.claw;
  const governance = data?.governance;
  const pipeline = data?.pipeline;

  if (loading) {
    return <div className="text-[#A8A29E] text-sm">Loading fleet data...</div>;
  }

  if (!data || (!claw?.registeredAgents && !governance)) {
    return (
      <div className="text-[#A8A29E] text-sm p-4">
        <p className="font-medium text-[#C9D1D9] mb-2">Fleet monitoring</p>
        <p>Fleet monitoring shows agent status across distributed deployments. Connect a fleet coordinator to enable cross-machine governance visibility.</p>
        <p className="mt-2 text-xs">Set FLEET_COORDINATOR_URL in your environment to enable.</p>
      </div>
    );
  }

  const agents = claw?.registeredAgents ?? 0;
  const quarantined = claw?.quarantinedAgents ?? 0;
  const haltActive = claw?.fleetHaltActive ?? false;
  const steeringActive = claw?.contextSteering?.active ?? false;
  const pipelineActive = pipeline?.stageCount === 10;
  const pipelineStages = pipeline?.stages ?? [];

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Fleet Overview</h2>

      {/* Badges row */}
      <div className="flex gap-3 flex-wrap">
        <Badge variant="outline" style={{ color: GovernanceColors.healthy, borderColor: GovernanceColors.healthy }}>
          <CheckCircle aria-hidden="true" className="w-3 h-3 mr-1 inline" />
          {agents} Registered
        </Badge>
        <Badge variant="outline" style={quarantined > 0 ? { color: GovernanceColors.quarantined, borderColor: GovernanceColors.quarantined } : { color: '#A8A29E', borderColor: '#4A443D' }}>
          {quarantined > 0 && <AlertTriangle aria-hidden="true" className="w-3 h-3 mr-1 inline" />}
          {quarantined} Quarantined
        </Badge>
        <Badge variant="outline" style={haltActive ? { color: GovernanceColors.critical, borderColor: GovernanceColors.critical } : { color: GovernanceColors.healthy, borderColor: GovernanceColors.healthy }}>
          {haltActive
            ? <XCircle aria-hidden="true" className="w-3 h-3 mr-1 inline" />
            : <CheckCircle aria-hidden="true" className="w-3 h-3 mr-1 inline" />}
          Fleet {haltActive ? 'HALTED' : 'Active'}
        </Badge>
        <Badge variant="outline" style={steeringActive ? { color: GovernanceColors.streaming, borderColor: GovernanceColors.streaming } : { color: '#A8A29E', borderColor: '#4A443D' }}>
          Steering {steeringActive ? 'ON' : 'OFF'}
        </Badge>
        <Badge variant="outline" style={pipelineActive ? { color: GovernanceColors.primaryLight, borderColor: GovernanceColors.primaryLight } : { color: '#A8A29E', borderColor: '#4A443D' }}>
          <Layers aria-hidden="true" className="w-3 h-3 mr-1 inline" />
          Pipeline {pipelineActive ? `${pipeline.stageCount} Stages` : 'Offline'}
        </Badge>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users aria-hidden="true" className="w-4 h-4" style={{ color: GovernanceColors.primary }} />
              <span className="text-sm text-[#A8A29E]">Total Agents</span>
            </div>
            <span className="text-2xl font-bold">{agents}</span>
          </CardContent>
        </Card>
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle aria-hidden="true" className="w-4 h-4" style={{ color: GovernanceColors.quarantined }} />
              <span className="text-sm text-[#A8A29E]">Quarantined</span>
            </div>
            <span className="text-2xl font-bold" style={quarantined > 0 ? { color: GovernanceColors.quarantined } : undefined}>{quarantined}</span>
          </CardContent>
        </Card>
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap aria-hidden="true" className="w-4 h-4" style={{ color: GovernanceColors.streaming }} />
              <span className="text-sm text-[#A8A29E]">Context Steering</span>
            </div>
            <span className={`text-2xl font-bold ${steeringActive ? '' : 'text-[#8F857B]'}`} style={steeringActive ? { color: GovernanceColors.healthy } : undefined}>
              {steeringActive ? 'Active' : 'Inactive'}
            </span>
          </CardContent>
        </Card>
      </div>

      {/* Governance Pipeline */}
      {pipelineActive && (
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Layers aria-hidden="true" className="w-4 h-4" style={{ color: GovernanceColors.primaryLight }} />
              <span className="text-sm font-medium">Governance Pipeline</span>
              <span className="text-xs text-[#8F857B] ml-auto">10-stage forensic evidence chain</span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {pipelineStages.map((stage: string, i: number) => {
                const [num, ...nameParts] = stage.split(': ');
                const name = nameParts.join(': ');
                const stageNum = i + 1;
                const isActive = stageHighlight.active.has(stageNum);
                const isDeny = stageHighlight.deny === stageNum;
                // When demoActive: active stages glow with stageHighlight.color (or red for deny);
                // inactive stages stay default. Adds a transition + small scale-up so the
                // pipeline visibly engages when Run Demo flows a governance decision through.
                const borderColor = isDeny ? GovernanceColors.critical : isActive ? stageHighlight.color : '#3A352F';
                const bgColor = isActive ? `${stageHighlight.color}22` : '#1C1917';
                const numColor = isDeny ? GovernanceColors.critical : isActive ? stageHighlight.color : GovernanceColors.primaryLight;
                return (
                  <div
                    key={i}
                    className="text-center p-2 rounded transition-all duration-300"
                    style={{
                      border: `1px solid ${borderColor}`,
                      background: bgColor,
                      transform: isActive ? 'scale(1.04)' : 'scale(1)',
                      boxShadow: isActive ? `0 0 12px ${stageHighlight.color}55` : 'none',
                    }}
                  >
                    <span className="text-xs font-mono" style={{ color: numColor }}>{num}</span>
                    <p className="text-[10px] text-[#A8A29E] mt-1 leading-tight">{name}</p>
                    {isDeny && <p className="text-[9px] text-red-300 mt-0.5 font-bold">DENIED</p>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tier-aware feature visibility -- read /api/tiers/{userTier} and render
          unlocked + locked-at-next features by category with upgrade CTA */}
      <Card className="bg-[#292524] border-[#3A352F]">
        <CardContent className="p-4">
          <TierFeatureGrid />
        </CardContent>
      </Card>

      {/* Agents table */}
      <Card className="bg-[#292524] border-[#3A352F]">
        <CardContent className="p-4">
          <h3 className="text-sm font-medium mb-3">Registered Agents</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Governed agent fleet status</caption>
              <thead>
                <tr className="border-b border-[#3A352F] text-[#A8A29E]">
                  <th scope="col" className="text-left py-2 px-3 font-medium">Agent ID</th>
                  <th scope="col" className="text-left py-2 px-3 font-medium">Tier</th>
                  <th scope="col" className="text-left py-2 px-3 font-medium">Status</th>
                  <th scope="col" className="text-left py-2 px-3 font-medium">Last Action</th>
                </tr>
              </thead>
              <tbody>
                {governance?.agentMonitor?.monitoredAgents === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-4 text-center text-[#8F857B]">
                      No agents currently registered. Deploy agents to see fleet status.
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={4} className="py-4 text-center text-[#8F857B]">
                      Agent details available when fleet is active.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Auto-refresh indicator */}
      <div className="text-xs text-[#8F857B] flex items-center gap-1">
        <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: GovernanceColors.healthy }} />
        Auto-refreshing every 5 seconds
      </div>
    </div>
  );
}
